import { Locale } from '@headless-workspace/config';
import { ImageWithSrc } from '@headless-workspace/typings';
import { ObjectHelpers } from '@headless-workspace/utils';
import {
    BooleanFormField,
    FormFieldValue,
    isSelectField,
    mapBaseFormField,
    mapSelectFormField,
    NumberFormField,
    SelectFormField,
    TextFormField,
} from '../FormValues';
import { ProductFormDTO, ProductFormTemplateDTO } from './ProductFormDTO';

export type ProductFormValue = {
    id: string;
    name: string;
    image?: ImageWithSrc;
    brand: {
        name: string;
    };
};

export type ProductFormTemplateValue = {
    product: ProductFormValue;
    fields: Record<string, FormFieldValue>;
};

export type ProductFormParams = {
    locale: Locale;
    productId: string;
};

export enum ProductFormId {
    Engrave = 'engrave',
    Review = 'review',
}

export type RatingAndReviewsFormValue = ProductFormTemplateValue & {
    fields: {
        title: TextFormField;
        nickname: TextFormField;
        content: TextFormField;
        rating: NumberFormField;
        recommended: BooleanFormField;
        acceptedTermsAndConditions: BooleanFormField;
        age: SelectFormField;
        usageDuration: SelectFormField;
    };
};

export type EngraveFormValue = ProductFormTemplateValue & {
    fields: { font: SelectFormField; line1: TextFormField; line2?: TextFormField; line3?: TextFormField };
};

export const mapProductForm = (dto: ProductFormDTO): ProductFormValue => {
    const { imageUrl, ...filteredDto } = dto;

    return {
        ...filteredDto,
        image: dto.imageUrl ? ObjectHelpers.createImageWithSrc(dto.imageUrl, dto.name) : undefined,
    };
};

// Signature overloads to handle all ProductForms with one function
export function mapProductTemplateForm(
    id: ProductFormId.Review,
    dto: ProductFormTemplateDTO,
): RatingAndReviewsFormValue;
export function mapProductTemplateForm(id: ProductFormId.Engrave, dto: ProductFormTemplateDTO): EngraveFormValue;
export function mapProductTemplateForm(id: ProductFormId, dto: ProductFormTemplateDTO): ProductFormTemplateValue;

// Implementation
export function mapProductTemplateForm(
    _: ProductFormId,
    { form: { product, fields } }: ProductFormTemplateDTO,
): RatingAndReviewsFormValue | EngraveFormValue | ProductFormTemplateValue {
    return {
        product: mapProductForm(product),
        fields: Object.fromEntries(
            fields.map((field) => [
                field.id,
                isSelectField(field) ? mapSelectFormField(field) : mapBaseFormField(field),
            ]),
        ),
    } as ProductFormTemplateValue;
}
